import { preparePreloadableRoutes } from "@loop-payments/react-router-relay"
import React, { useMemo, useRef } from "react"
import { useRelayEnvironment } from "react-relay";
import { createBrowserRouter, RouterProvider } from "react-router"
import type { MeUserDataType } from "src/app"

import ErrorPage from "./components/error-page"
import { InternFooter } from "./components/InternFooter/InternFooter"
import { getDefaultLayout } from "./components/layout"
import { APP_ROUTES } from "./routes-config"


// Wraps a route's rendered element with the InternFooter sibling. The footer
// uses `useLocation` and `useLazyLoadQuery`, both of which require being inside
// `<RouterProvider>` / `<RelayEnvironmentProvider>` — wrapping at the
// per-route element level ensures both contexts are available. The footer
// self-gates to super-admins + matching routes, so this is a no-op for
// everyone else.
function withInternFooter(element: React.ReactNode): React.ReactElement {
  return (
    <>
      {element}
      <InternFooter />
    </>
  );
}

export default function TierZeroAppRouter({ me }: { me: MeUserDataType }) {
  const environment = useRelayEnvironment();
  const environmentRef = useRef(environment);
  environmentRef.current = environment;

  const router = useMemo(() => {
    const innerRoutes = preparePreloadableRoutes(APP_ROUTES, {
      getEnvironment() {
        return environmentRef.current;
      },
    });
    const routes = innerRoutes.map((router) => {
      // Skip layout processing for routes that already have an element (like redirects)
      if (router.element) {
        return {
          ...router,
          element: withInternFooter(router.element),
          ErrorBoundary: ErrorPage,
        };
      }

      // @ts-ignore TODO: better type support
      const getLayout = router.Component?.getLayout || getDefaultLayout;
      const Component = router.Component!;
      const page = getLayout(<Component />, me);
      return {
        ...router,
        element: withInternFooter(page),
        Component: null,
        ErrorBoundary: ErrorPage,
      };
    });
    return createBrowserRouter(routes);
  }, [me]);

  return <RouterProvider router={router} />;
}
